import type { operations } from '@multaqa/api-client'
import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

type TrialBalanceBody =
  operations['trialBalance.show']['responses'][200]['content']['application/json']

/**
 * The four-column trial balance row, derived from the `trialBalance.show`
 * response. Locale-keyed `name` renders with `localizedText`; `parent_id` and
 * `is_postable` are the hierarchy metadata of the server-computed roll-up row.
 */
export type TrialBalanceRow = TrialBalanceBody['data'][number]

export type TrialBalanceMeta = TrialBalanceBody['meta']

export function useTrialBalance(
  companyId: string | null,
  from: string,
  to: string,
  options?: Omit<
    UseQueryOptions<{ rows: TrialBalanceRow[]; meta: TrialBalanceMeta }, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<{ rows: TrialBalanceRow[]; meta: TrialBalanceMeta }, Error>({
    queryKey: ['companies', companyId, 'accounting', 'trial-balance', from, to] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async () => {
      if (!companyId) return { rows: [], meta: { from, to } }
      const response = await api.GET('/accounting/reports/trial-balance', {
        headers: { 'X-Company-Id': companyId },
        // @ts-expect-error - Scramble missed the controller's manual `Request::query` reads, so the
        // generated type is `query?: never` while the wire does need `from`/`to`.
        params: { query: { from, to } },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load trial balance')
      const body = response.data ?? { data: [], meta: { from, to } }
      return { rows: body.data ?? [], meta: body.meta ?? { from, to } }
    },
  })
}
